import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const ALERT_TYPES = [
  { id: 'mix_substitution',    label: 'MIX → VS',       hint: 'Auto-substituted at DV complete' },
  { id: 'mix_diverted_history', label: 'History review', hint: 'Pre-fix DV→CSV diversions to inspect' },
  { id: 'size_map_gap',        label: 'Size gaps',      hint: 'Diamond size codes with no price mapping' },
];

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN'); } catch { return iso; }
};

const AlertRow = ({ alert, onAck, isAcking }) => {
  const ctx = alert.context || {};
  const rows = ctx.rows || [];
  return (
    <div
      className={`border rounded-xl p-3 mb-2 ${alert.acknowledged ? 'bg-white border-gray-200 opacity-70' : 'bg-amber-50 border-amber-300'}`}
      data-testid={`alert-row-${alert._id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Star className={`w-4 h-4 mt-0.5 flex-shrink-0 ${alert.acknowledged ? 'text-gray-400' : 'text-amber-600'}`} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 break-words">{alert.subject}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {fmtTs(alert.created_at)}
              {alert.source_user_email && ` · ${alert.source_user_email}`}
              {ctx.verifier && ` · verifier: ${ctx.verifier}`}
              {alert.tag_number && ` · tag: ${alert.tag_number}`}
              {ctx.row_count !== undefined && ` · ${ctx.row_count} row${ctx.row_count === 1 ? '' : 's'}`}
              {ctx.diverted_count !== undefined && ` · ${ctx.diverted_count} diverted`}
            </div>
          </div>
        </div>
        {!alert.acknowledged ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={isAcking}
            onClick={() => onAck(alert._id)}
            data-testid={`ack-btn-${alert._id}`}
          >
            <CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledge
          </Button>
        ) : (
          <span className="text-[11px] text-gray-500 whitespace-nowrap">
            ✓ {alert.acknowledged_by || 'admin'} · {fmtTs(alert.acknowledged_at)}
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <div className="mt-2 pl-6">
          <div className="text-[11px] text-slate-600 mb-1 font-semibold">Substituted rows:</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-gray-500 text-left">
                <tr>
                  <th className="pr-2 py-0.5">Serial</th>
                  <th className="pr-2 py-0.5">Stone code</th>
                  <th className="pr-2 py-0.5">Shape · Size</th>
                  <th className="pr-2 py-0.5">Colours</th>
                  <th className="pr-2 py-0.5">Clarity</th>
                  <th className="pr-2 py-0.5">Wt · Pcs</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-amber-200/50">
                    <td className="pr-2 py-0.5">{r.serial_no ?? '—'}</td>
                    <td className="pr-2 py-0.5 font-mono">{r.stone_code || '—'}</td>
                    <td className="pr-2 py-0.5">{r.shape || '—'} · {r.size || '—'}</td>
                    <td className="pr-2 py-0.5">{(r.colours || []).join(', ')}</td>
                    <td className="pr-2 py-0.5">
                      <span className="px-1 rounded bg-amber-200/60 text-amber-900">
                        {(r.clarities || []).join(', ')} → VS
                      </span>
                    </td>
                    <td className="pr-2 py-0.5">{r.weight_cts ?? '—'} · {r.pieces ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {ctx.gaps && ctx.gaps.length > 0 && (
        <div className="mt-2 pl-6">
          <div className="text-[11px] text-red-700 mb-1 font-semibold">Unmapped diamond size codes (would price at ₹0):</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-gray-500 text-left">
                <tr>
                  <th className="pr-2 py-0.5">Size code</th>
                  <th className="pr-2 py-0.5">Shapes</th>
                  <th className="pr-2 py-0.5">Sieve letters</th>
                </tr>
              </thead>
              <tbody>
                {ctx.gaps.map((g, i) => (
                  <tr key={i} className="border-t border-red-200/60">
                    <td className="pr-2 py-0.5 font-mono font-bold text-red-700">{g.chip}</td>
                    <td className="pr-2 py-0.5">{(g.shapes || []).join(', ')}</td>
                    <td className="pr-2 py-0.5 font-mono">{(g.sieve_letters || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-600 mt-1">Fix: add these codes to <span className="font-mono">SIZE_MAP</span> in the diamond rate resolver.</div>
        </div>
      )}
      {ctx.erp_pill_indexes && ctx.erp_pill_indexes.length > 0 && (
        <div className="mt-2 pl-6 text-[11px] text-slate-600">
          Diverted pill serials: <span className="font-mono">{ctx.erp_pill_indexes.join(', ')}</span>
          {ctx.csv_status && <> · CSV status: <b>{ctx.csv_status}</b></>}
        </div>
      )}
    </div>
  );
};

const MixSubstitutions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('mix_substitution');
  const [onlyUnacked, setOnlyUnacked] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ mix_substitution: 0, mix_diverted_history: 0, size_map_gap: 0, total: 0 });
  const [acking, setAcking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countsRes] = await Promise.all([
        api.get('/admin/alerts', { params: { type: tab, only_unacked: onlyUnacked, limit: 200 } }),
        api.get('/admin/alerts/counts'),
      ]);
      setAlerts(listRes.data.alerts || []);
      setCounts({
        mix_substitution:      countsRes.data.mix_substitution ?? 0,
        mix_diverted_history:  countsRes.data.mix_diverted_history ?? 0,
        size_map_gap:          countsRes.data.size_map_gap ?? 0,
        total:                 countsRes.data.total ?? 0,
      });
    } catch (e) {
      toast({ title: 'Failed to load alerts', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tab, onlyUnacked]);

  useEffect(() => { load(); }, [load]);

  const ackOne = async (alertId) => {
    setAcking(alertId);
    try {
      await api.post(`/admin/alerts/${alertId}/ack`);
      await load();
    } catch (e) {
      toast({ title: 'Acknowledge failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAcking(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mix-substitutions-page">
      <div className="bg-slate-800 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-slate-700" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">MIX Substitutions</h1>
              <p className="text-xs text-slate-300">DVs where a MIX clarity was auto-priced as VS of the same colour</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={load} className="text-white hover:bg-slate-700" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-3 lg:p-4 max-w-4xl mx-auto">
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3 text-xs text-amber-900 flex gap-2" data-testid="info-banner">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 mt-0.5" />
          <div>
            <b>How this works:</b> When a DV is completed with any MIX-clarity row, the pricing engine folds it to VS of the same colour and logs an alert here. Review the tags below; use <b>Acknowledge</b> once you've verified pricing is correct.
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 mb-3">
            {ALERT_TYPES.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}>
                {t.label}
                {counts[t.id] > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid={`badge-${t.id}`}>
                    {counts[t.id]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center justify-between mb-2 text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyUnacked}
              onChange={(e) => setOnlyUnacked(e.target.checked)}
              data-testid="only-unacked-toggle"
            />
            <span className="text-slate-700">Unacknowledged only</span>
          </label>
          <span className="text-gray-500">
            Showing {alerts.length}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <div className="w-10 h-10 border-4 border-slate-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-500 mt-3 text-sm">Loading…</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-10 bg-white border rounded-xl" data-testid="empty-state">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-slate-700 text-sm font-semibold">Nothing to review</p>
            <p className="text-gray-500 text-xs mt-1">
              {onlyUnacked ? 'All caught up! Uncheck "Unacknowledged only" to see past items.' : 'No alerts of this type yet.'}
            </p>
          </div>
        ) : (
          <div data-testid="alerts-list">
            {alerts.map((a) => (
              <AlertRow key={a._id} alert={a} onAck={ackOne} isAcking={acking === a._id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MixSubstitutions;
